import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    getTemplateBody,
    getTemplateDisplayName,
    getTemplateVariableLabel,
    readRecentTemplateSids,
    rememberTemplateSid,
    sortTemplatesRecentFirst,
} from '../../lib/communicationsTemplates';
import TemplatePreview from './TemplatePreview';
import { MAX_PINNED_TEMPLATES, usePinnedTemplatePreference } from '../../lib/pinnedTemplatePreference';
import { requestOpenTemplateSettings, templateSettingsHref } from '../../lib/templateSettingsReturn';
import useAppNavigate from '../../hooks/useAppNavigate';

export { templateSettingsHref };

// Comms next pass (1.12.214-beta): only WhatsApp templates are approved by anyone (Meta). SMS and email templates go live
// on save (mapTwilioApprovalToTemplateStatus), so their picker talks about "your templates", never approval.
export function templatePickerCopy(channel) {
    if (String(channel || '').toLowerCase() === 'whatsapp') {
        return {
            search: 'Search approved templates',
            loading: 'Loading approved templates…',
            empty: 'No approved templates yet — create one in Settings → Templates.',
            list: 'Approved templates',
        };
    }
    return {
        search: 'Search your templates',
        loading: 'Loading your templates…',
        empty: 'No templates yet — create one in Settings → Templates',
        list: 'Your templates',
    };
}

/** The Meta badge for a WhatsApp template; null for SMS / email (no approval step). */
export function templateApprovalBadge(template, channel) {
    const templateChannel = String(template?.channel || channel || '').toLowerCase();
    if (templateChannel !== 'whatsapp') return null;
    const status = String(template?.status || template?.approval_status || 'approved').toLowerCase();
    if (status === 'approved' || status === 'live') {
        return { label: 'Approved by Meta', className: 'bg-[#DCFCE7] text-[#14532D]' };
    }
    if (status === 'pending' || status === 'received' || status === 'in_review') {
        return { label: 'Pending Meta review', className: 'bg-[#FEF3C7] text-[#78350F]' };
    }
    return null;
}

function InlineTemplatePicker({
    templates = [],
    loading = false,
    selectedSid = '',
    variables = {},
    onSelect,
    onVariableChange,
    userId = null,
    orgId = null,
    recentSids = [],
    channel = '',
    theme = 'light',
    // Settings → Templates is admin-only: the links show for admins only.
    canEditTemplates = true,
    autoFocusSearch = false,
    // BL-177 Step 3: when the host already shows the template's values (the New message card), the fill-ins are not repeated here.
    hideFillIns = false,
}) {
    // In-app, through the open composer (it keeps the unsent draft); a full page load would lose it.
    const appNavigate = useAppNavigate();
    const openSettings = (targetChannel, templateId) => {
        if (!requestOpenTemplateSettings(targetChannel, templateId)) appNavigate(templateSettingsHref(targetChannel, templateId));
    };
    const dark = theme === 'dark';
    const copy = templatePickerCopy(channel);
    const storageKey = userId && orgId ? `communications-recent-templates:${orgId}:${userId}` : '';
    const [query, setQuery] = useState('');
    const [pinError, setPinError] = useState('');
    const searchRef = useRef(null);
    const pinnedPreference = usePinnedTemplatePreference(userId);
    const localRecent = readRecentTemplateSids(storageKey);
    const recent = [...recentSids, ...localRecent].filter((sid, index, list) => sid && list.indexOf(sid) === index);
    const channelTemplates = useMemo(() => templates.filter((template) => (
        !channel || !template?.channel || template.channel === channel
    )), [channel, templates]);
    const visibleTemplates = useMemo(() => sortTemplatesRecentFirst(
        channelTemplates
        .filter((template) => `${getTemplateDisplayName(template)} ${template.name || ''} ${getTemplateBody(template)}`.toLowerCase().includes(query.trim().toLowerCase())),
        recent
    ), [channelTemplates, query, recent]);
    const selected = channelTemplates.find((template) => template.sid === selectedSid) || null;
    const selectedIsPinned = Boolean(selected && pinnedPreference.ids.includes(selected.sid));
    const variableKeys = [...new Set([
        ...[...getTemplateBody(selected).matchAll(/\{\{\s*([^{}]+?)\s*\}\}/g)].map((match) => match[1]),
        ...Object.keys(selected?.variables || {}),
    ])];
    const inputClass = `w-full rounded-lg border px-3 py-2 text-sm ${dark
        ? 'border-gray-600 bg-gray-900 text-gray-100 placeholder-gray-500'
        : 'border-gray-300 bg-white text-gray-800 placeholder-gray-400'}`;

    const choose = (template) => {
        rememberTemplateSid(storageKey, template.sid);
        onSelect?.(template);
    };
    const toggleSelectedPin = async () => {
        if (!selected || !userId) return;
        const next = selectedIsPinned
            ? pinnedPreference.ids.filter((sid) => sid !== selected.sid)
            : [...pinnedPreference.ids, selected.sid];
        try {
            await pinnedPreference.save(next);
            setPinError('');
        } catch (error) {
            setPinError(error.message || 'Could not update pinned templates.');
        }
    };

    useEffect(() => {
        if (autoFocusSearch) searchRef.current?.focus();
    }, [autoFocusSearch]);

    return (
        <div className="space-y-2" aria-label="Template picker">
            <label className="sr-only" htmlFor="communications-template-search">Search templates</label>
            <input
                ref={searchRef}
                id="communications-template-search"
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={copy.search}
                aria-label="Search templates"
                className={inputClass}
            />
            {loading ? (
                <p className="text-xs text-gray-500">{copy.loading}</p>
            ) : templates.length === 0 ? (
                <p className="text-xs text-gray-500">{copy.empty}</p>
            ) : visibleTemplates.length === 0 ? (
                <p className="text-xs text-gray-500">No templates match your search.</p>
            ) : (
                <div className={`max-h-44 overflow-y-auto rounded-lg border ${dark ? 'border-gray-700' : 'border-gray-200'}`} role="listbox" aria-label={copy.list}>
                    {visibleTemplates.map((template) => {
                        const badge = templateApprovalBadge(template, channel);
                        return (
                        <button
                            key={template.sid}
                            type="button"
                            role="option"
                            aria-selected={selectedSid === template.sid}
                            onClick={() => choose(template)}
                            className={`block w-full border-l-4 px-3 py-2 text-left ${selectedSid === template.sid
                                ? 'border-l-[#03B7C9] bg-[#03B7C9]/10'
                                : 'border-l-transparent'
                            } ${dark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}
                        >
                            <span className="flex items-center justify-between gap-2">
                                <span className="truncate text-xs">
                                    {getTemplateDisplayName(template)}
                                </span>
                                {badge && (
                                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${badge.className}`}>{badge.label}</span>
                                )}
                            </span>
                            <span className="mt-1 block truncate text-xs text-gray-500">{getTemplateBody(template) || 'No preview available'}</span>
                        </button>
                        );
                    })}
                </div>
            )}
            {pinError && <p role="alert" className="text-xs text-red-500">{pinError}</p>}
            {pinnedPreference.error && <p role="alert" className="text-xs text-red-500">{pinnedPreference.error}</p>}
            {selected && (
                <>
                    <div className="flex items-center justify-between gap-2">
                        <span className="text-xs text-gray-500">
                            {selectedIsPinned ? 'Pinned for your quick menu' : `${pinnedPreference.ids.length}/${MAX_PINNED_TEMPLATES} quick-menu pins`}
                        </span>
                        <button
                            type="button"
                            onClick={toggleSelectedPin}
                            disabled={!userId || pinnedPreference.saving
                                || (!selectedIsPinned && pinnedPreference.ids.length >= MAX_PINNED_TEMPLATES)}
                            aria-pressed={selectedIsPinned}
                            className="rounded-full border border-[#03B7C9]/50 px-2 py-1 text-xs text-[#02808D] disabled:opacity-50"
                        >
                            {selectedIsPinned ? 'Unpin' : 'Pin for quick menu'}
                        </button>
                    </div>
                    {!hideFillIns && <h4 className="text-xs font-semibold uppercase tracking-wide">Fill-ins</h4>}
                    {!hideFillIns && <div className="ia-comms-template-grid">
                        {variableKeys.map((key, index) => {
                            const value = variables[key] || '';
                            return (
                                <label key={key} className="block text-xs font-medium text-gray-600">
                                    {getTemplateVariableLabel(selected, key, index)}
                                    <input
                                        value={value}
                                        onChange={(event) => onVariableChange?.(key, event.target.value)}
                                        aria-label={`Template variable ${key}`}
                                        className={`${inputClass} mt-1 ${value.trim() ? '' : 'border-amber-400 bg-amber-50'}`}
                                    />
                                </label>
                            );
                        })}
                    </div>}
                    <div className="space-y-2">
                        <div className="rounded-lg bg-[#ECE5DD] p-3" aria-label="Template preview">
                            <TemplatePreview
                                template={{
                                    ...selected,
                                    variables: Object.fromEntries(variableKeys.map((key) => [key, { sample: variables[key] || '' }])),
                                }}
                                theme={theme}
                            />
                        </div>
                        {canEditTemplates && (
                            <button
                                type="button"
                                onClick={() => openSettings(selected.channel || channel, selected.id || '')}
                                className={`inline-block text-left text-xs font-medium underline ${dark ? "text-[#67E8F9]" : "text-[#02707B]"}`}
                                data-template-settings-link="edit"
                            >
                                Edit wording in Template Builder
                            </button>
                        )}
                    </div>
                </>
            )}
            {canEditTemplates && (
                <button
                    type="button"
                    onClick={() => openSettings(channel, '')}
                    className={`inline-block text-left text-xs font-medium underline ${dark ? "text-[#67E8F9]" : "text-[#02707B]"}`}
                    data-template-settings-link="manage"
                >
                    Manage templates → Settings › Templates
                </button>
            )}
        </div>
    );
}

export default InlineTemplatePicker;
